import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Dropdown, Input, message, Modal, Select, Skeleton, Tooltip, Upload } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileOutlined,
  MoreOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService";
import Text from "../../common/typography/text";
import ManufacturerProduct from "./ManufacturerProduct";
import ManufacturerAccounts from "./ManufacturerAccounts";
import ManufacturerUsers from "./ManufacturerUsers";
import { colors } from "../../theme/colors.js";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";

const MAX_MANUFACTURER_FILE_BYTES = 4 * 1024 * 1024;

const emptyEditForm = {
  ManufacturerName: "",
  AddressLine1: "",
  AddressLine2: "",
  City: "",
  State: "",
  Zip: "",
  ManufacturerEmailDO: "",
  ManufacturerEmailCO: "",
  ManufacturerEmailPU: "",
  ManufacturerVisibilityDO: false,
  ManufacturerVisibilityCO: false,
  ManufacturerVisibilityPU: false,
  NeedsApproval: false,
  ASInvoice: false,
  CollectPayment: false,
};

const boolValue = (value) => value === true || value === 1 || value === "1";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = moment(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "N/A";
};

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "N/A";
};

const DetailItem = ({ label, children }) => (
  <div className="manufacturer-detail-item">
    <Text variant="smallText" color="lightText">
      {label}
    </Text>
    <Text variant="smallText" color="darkText">
      {children}
    </Text>
  </div>
);

const StatusPill = ({ active, trueLabel = "Yes", falseLabel = "No" }) => (
  <span className={`manufacturer-status-pill ${active ? "success" : "failure"}`}>
    {active ? trueLabel : falseLabel}
  </span>
);

const ToggleField = ({ label, value, onChange, disabled = false }) => (
  <button
    aria-disabled={disabled}
    className={`manufacturer-edit-toggle-card ${value ? "active" : ""} ${disabled ? "disabled" : ""}`}
    disabled={disabled}
    onClick={() => onChange(!value)}
    type="button"
  >
    <span>{label}</span>
    <span className="manufacturer-edit-toggle-track">
      <span className="manufacturer-edit-toggle-knob">
        {value ? "✓" : "×"}
      </span>
    </span>
  </button>
);

const EditManufacturerModal = ({
  manufacturer,
  open,
  onClose,
  onSaved,
  isCreateMode = false,
}) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(emptyEditForm);
  const [stateOptions, setStateOptions] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const apiServe = new APIService();
        const response = await apiServe.getDataFromBackend("getstatesmaster");
        const options = (response?.data?.data || [])
          .filter((state) => state?.Abbreviation)
          .map((state) => ({
            value: state.Abbreviation,
            label: state.Abbreviation,
          }));
        setStateOptions(options);
      } catch (error) {
        message.error("Unable to load states.");
        setStateOptions([]);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStates();
  }, [open]);

  useEffect(() => {
    if (!open || !manufacturer) return;
    setFormValues({
      ManufacturerName: manufacturer.ManufacturerName || "",
      AddressLine1: manufacturer.AddressLine1 || "",
      AddressLine2: manufacturer.AddressLine2 || "",
      City: manufacturer.City || "",
      State: manufacturer.State || "",
      Zip: manufacturer.Zip || "",
      ManufacturerEmailDO: manufacturer.ManufacturerEmailDO || "",
      ManufacturerEmailCO: manufacturer.ManufacturerEmailCO || "",
      ManufacturerEmailPU: manufacturer.ManufacturerEmailPU || "",
      ManufacturerVisibilityDO: boolValue(manufacturer.ManufacturerVisibilityDO),
      ManufacturerVisibilityCO: boolValue(manufacturer.ManufacturerVisibilityCO),
      ManufacturerVisibilityPU: boolValue(manufacturer.ManufacturerVisibilityPU),
      NeedsApproval: boolValue(manufacturer.NeedsApproval),
      ASInvoice: boolValue(manufacturer.ASInvoice),
      CollectPayment: boolValue(manufacturer.CollectPayment),
    });
  }, [manufacturer, open]);

  const setField = (field, value) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
      ...(field === "ASInvoice" && !value ? { CollectPayment: false } : {}),
    }));
  };

  const validate = () => {
    const requiredFields = [
      ["ManufacturerName", "Manufacturer Name"],
      ["AddressLine1", "Address Line 1"],
      ["City", "City"],
      ["State", "State"],
      ["Zip", "ZIP"],
    ];
    const missing = requiredFields.find(([field]) => !String(formValues[field] || "").trim());
    if (missing) {
      message.error(`${missing[1]} is required.`);
      return false;
    }
    return true;
  };

  const handleClose = () => {
    if (isCreateMode) {
      navigate("/ManufacturerDashboard", { replace: true });
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const apiServe = new APIService();
      const payload = isCreateMode
        ? formValues
        : {
            ID: manufacturer.ID,
            ...formValues,
          };
      await apiServe.addDataToBackend(
        payload,
        isCreateMode ? "addmanufacturer" : "updatemanufacturer",
      );
      message.success(isCreateMode ? "Manufacturer added." : "Manufacturer updated.");
      if (isCreateMode) {
        navigate("/ManufacturerDashboard", { replace: true });
        return;
      }
      onClose();
      await onSaved?.();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to save manufacturer.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      centered
      footer={null}
      closable={false}
      width={600}
      onCancel={handleClose}
      className="manufacturer-edit-modal-shell"
      destroyOnClose
    >
      <div className="manufacturer-edit-modal">
        <div className="manufacturer-edit-modal-header">
          <Text variant="h1">{isCreateMode ? "Add Manufacturer" : "Edit Manufacturer"}</Text>
          <button className="manufacturer-icon-button" onClick={handleClose} type="button">
            <CloseOutlined />
          </button>
        </div>
        <div className="manufacturer-edit-divider" />

        <div className="manufacturer-edit-field full">
          <label>
            Manufacturer Name<span>*</span>
          </label>
          <Input
            value={formValues.ManufacturerName}
            onChange={(event) => setField("ManufacturerName", event.target.value)}
            placeholder="Manufacturer Name"
          />
        </div>

        <div className="manufacturer-edit-grid two">
          <div className="manufacturer-edit-field">
            <label>
              Address Line 1<span>*</span>
            </label>
            <Input
              value={formValues.AddressLine1}
              onChange={(event) => setField("AddressLine1", event.target.value)}
              placeholder="Address Line 1"
            />
          </div>
          <div className="manufacturer-edit-field">
            <label>
              Address Line 2
            </label>
            <Input
              value={formValues.AddressLine2}
              onChange={(event) => setField("AddressLine2", event.target.value)}
              placeholder="Code"
            />
          </div>
        </div>

        <div className="manufacturer-edit-grid city-state-zip">
          <div className="manufacturer-edit-field">
            <label>
              City<span>*</span>
            </label>
            <Input
              value={formValues.City}
              onChange={(event) => setField("City", event.target.value)}
              placeholder="City"
            />
          </div>
          <div className="manufacturer-edit-field">
            <label>
              State<span>*</span>
            </label>
            <Select
              showSearch
              loading={statesLoading}
              optionFilterProp="label"
              options={stateOptions}
              placeholder="State"
              value={formValues.State}
              onChange={(value) => setField("State", value)}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
            />
          </div>
          <div className="manufacturer-edit-field">
            <label>
              ZIP<span>*</span>
            </label>
            <Input
              value={formValues.Zip}
              onChange={(event) => setField("Zip", event.target.value)}
              placeholder="ZIP"
            />
          </div>
        </div>

        <div className="manufacturer-edit-grid two">
          <div className="manufacturer-edit-field">
            <label>Direct Order Email</label>
            <Input
              value={formValues.ManufacturerEmailDO}
              onChange={(event) => setField("ManufacturerEmailDO", event.target.value)}
              placeholder="Direct Order Email"
            />
          </div>
          <div className="manufacturer-edit-field">
            <label>Consignment Order Email</label>
            <Input
              value={formValues.ManufacturerEmailCO}
              onChange={(event) => setField("ManufacturerEmailCO", event.target.value)}
              placeholder="Consignment Order Email"
            />
          </div>
        </div>

        <div className="manufacturer-edit-field full">
          <label>Product Usage Email</label>
          <Input
            value={formValues.ManufacturerEmailPU}
            onChange={(event) => setField("ManufacturerEmailPU", event.target.value)}
            placeholder="Product Usage Email"
          />
        </div>

        <div className="manufacturer-edit-section">
          <label>
            Check all the forms that this manufacturer is enabled for.<span>*</span>
          </label>
          <div className="manufacturer-edit-toggle-row">
            <ToggleField
              label="Direct Order"
              value={formValues.ManufacturerVisibilityDO}
              onChange={(value) => setField("ManufacturerVisibilityDO", value)}
            />
            <ToggleField
              label="Consignment Order"
              value={formValues.ManufacturerVisibilityCO}
              onChange={(value) => setField("ManufacturerVisibilityCO", value)}
            />
            <ToggleField
              label="Product Usage"
              value={formValues.ManufacturerVisibilityPU}
              onChange={(value) => setField("ManufacturerVisibilityPU", value)}
            />
          </div>
        </div>

        <div className="manufacturer-edit-section">
          <label>
            Settings<span>*</span>
          </label>
          <div className="manufacturer-edit-toggle-row">
            <ToggleField
              label="Needs Approval"
              value={formValues.NeedsApproval}
              onChange={(value) => setField("NeedsApproval", value)}
            />
            <ToggleField
              label="AS Invoice"
              value={formValues.ASInvoice}
              onChange={(value) => setField("ASInvoice", value)}
            />
            <ToggleField
              label="Collect Payment"
              value={formValues.CollectPayment}
              onChange={(value) => setField("CollectPayment", value)}
              disabled={!formValues.ASInvoice}
            />
          </div>
        </div>

        <div className="manufacturer-edit-modal-footer">
          <button className="manufacturer-secondary-button" onClick={handleClose} type="button">
            Cancel
          </button>
          <button
            className="manufacturer-save-button"
            disabled={saving}
            onClick={handleSave}
            type="button"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ManufacturerFilesSection = ({ manufacturerID, isAdmin, canAddFile }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchFiles = useCallback(async () => {
    if (!manufacturerID) return;
    try {
      setLoading(true);
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `getmanufacturerfiles?ManufacturerID=${manufacturerID}`,
      );
      setFiles(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      message.error("Unable to load manufacturer files.");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [manufacturerID]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const resetModal = () => {
    setModalOpen(false);
    setEditingFile(null);
    setDisplayName("");
    setSelectedFile(null);
    setUploadFileList([]);
  };

  const openAddModal = () => {
    if (!canAddFile) return;
    setEditingFile(null);
    setDisplayName("");
    setSelectedFile(null);
    setUploadFileList([]);
    setModalOpen(true);
  };

  const openEditModal = (file) => {
    setEditingFile(file);
    setDisplayName(file.DisplayName || file.FileName || "");
    setSelectedFile(null);
    setUploadFileList([
      {
        uid: String(file.ID),
        name: file.FileName || file.DisplayName || "manufacturer-file",
        status: "done",
      },
    ]);
    setModalOpen(true);
  };

  const validateSelectedFile = (file, requireFile) => {
    if (!file && requireFile) return "Choose a file to upload.";
    if (!file) return "";
    if (!file.size) return "File cannot be empty.";
    if (file.size > MAX_MANUFACTURER_FILE_BYTES) {
      return "File must be 4 MB or smaller.";
    }
    return "";
  };

  const restoreExistingFileList = () => {
    if (!editingFile) {
      setUploadFileList([]);
      return;
    }

    setUploadFileList([
      {
        uid: String(editingFile.ID),
        name: editingFile.FileName || editingFile.DisplayName || "manufacturer-file",
        status: "done",
      },
    ]);
  };

  const handleUploadFile = ({ file, onSuccess, onError }) => {
    const error = validateSelectedFile(file, !editingFile);
    if (error) {
      message.error(error);
      setSelectedFile(null);
      restoreExistingFileList();
      onError?.(new Error(error));
      return;
    }

    setSelectedFile(file);
    setUploadFileList([{ uid: file.uid || file.name, name: file.name, status: "done" }]);
    if (file && !displayName.trim()) {
      setDisplayName(file.name);
    }
    setTimeout(() => {
      onSuccess?.("ok");
    }, 0);
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    fileList: uploadFileList,
    customRequest: handleUploadFile,
    onRemove: () => {
      if (editingFile && !selectedFile) {
        return false;
      }
      setSelectedFile(null);
      restoreExistingFileList();
      return true;
    },
  };

  const submitFile = async () => {
    const fileToUpload = selectedFile;
    const fileError = validateSelectedFile(fileToUpload, !editingFile);
    if (fileError) {
      message.destroy();
      message.error(fileError);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("ManufacturerID", manufacturerID);
      formData.append("DisplayName", displayName.trim() || fileToUpload?.name || editingFile?.DisplayName || "");
      if (editingFile) {
        formData.append("ID", editingFile.ID);
      }
      if (fileToUpload) {
        formData.append("file", fileToUpload);
      }

      const apiServe = new APIService();
      await apiServe.addFileDataToBackend(
        formData,
        editingFile ? "updatemanufacturerfile" : "addmanufacturerfile",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      message.success(editingFile ? "File updated." : "File added.");
      resetModal();
      await fetchFiles();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to save file.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (file) => {
    if (!file?.BlobName) {
      message.error("File is missing storage metadata.");
      return;
    }
    try {
      const apiServe = new APIService();
      const sasUrl = await apiServe.getSasToken(file.BlobName);
      const exists = await apiServe.checkBlobExists(sasUrl);
      if (!exists) {
        message.error("File does not exist.");
        return;
      }

      const response = await axios.get(sasUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.FileName || file.DisplayName || "manufacturer-file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Unable to download file.");
    }
  };

  const deleteFile = async (file) => {
    const result = await Swal.fire({
      title: "Delete file?",
      text: "This will remove the file from this manufacturer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.failureColor,
      cancelButtonColor: colors.strokeDark,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const apiServe = new APIService();
      await apiServe.updateDataToBackend(
        { ID: file.ID, ManufacturerID: manufacturerID },
        "deletemanufacturerfile",
      );
      message.success("File deleted.");
      await fetchFiles();
    } catch (error) {
      message.error("Unable to delete file.");
    }
  };

  return (
    <section className="manufacturer-files-card">
      <div className="manufacturer-section-header">
        <Text variant="bodyText" color="darkText">
          Files
        </Text>
        {canAddFile && (
          <button className="manufacturer-small-primary-button" onClick={openAddModal} type="button">
            <PlusOutlined />
            Add File
          </button>
        )}
      </div>

      {loading ? (
        <div className="manufacturer-files-empty">
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </div>
      ) : files.length === 0 ? (
        <div className="manufacturer-files-empty">
          <Text variant="smallText" color="lightText">
            No files attached.
          </Text>
        </div>
      ) : (
        <div className="manufacturer-files-grid">
          {files.map((file) => (
            <div className="manufacturer-file-tile" key={file.ID}>
              <div className="manufacturer-file-icon">
                <FileOutlined />
              </div>
              <div className="manufacturer-file-text">
                <Text variant="tinyText" color="lightText">
                  {formatDate(file.updatedAt || file.createdAt)}
                </Text>
                <Tooltip title={file.DisplayName || file.FileName}>
                  <span className="manufacturer-file-name">
                    {file.DisplayName || file.FileName}
                  </span>
                </Tooltip>
              </div>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "download",
                      label: "Download",
                      icon: <DownloadOutlined />,
                      onClick: () => downloadFile(file),
                    },
                    ...(isAdmin
                      ? [
                          {
                            key: "edit",
                            label: "Update",
                            icon: <EditOutlined />,
                            onClick: () => openEditModal(file),
                          },
                          {
                            key: "delete",
                            label: "Delete",
                            icon: <DeleteOutlined />,
                            danger: true,
                            onClick: () => deleteFile(file),
                          },
                        ]
                      : []),
                  ],
                }}
                trigger={["click"]}
              >
                <button className="manufacturer-file-menu" type="button">
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        title={editingFile ? "Update File" : "Add File"}
        centered
        onCancel={resetModal}
        onOk={submitFile}
        okText={submitting ? "Saving..." : "Save"}
        okButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <div className="manufacturer-file-modal-body">
          <label className="manufacturer-file-modal-label">Display Name</label>
          <Input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Display name"
          />
          <label className="manufacturer-file-modal-label">File</label>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
          <Text variant="tinyText" color="lightText">
            Maximum file size: 4 MB.
          </Text>
        </div>
      </Modal>
    </section>
  );
};

const ManufacturerDetailsTab = ({ manufacturerDetails, onManufacturerUpdated }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const userData = user;
  const [manufacturerData, setManufacturerData] = useState(manufacturerDetails);
  const [loading, setLoading] = useState(!manufacturerDetails);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const isAdmin = userData?.Type === "Admin";
  const canEditManufacturer =
    isAdmin && hasPermission(userData, "manage_manufacturers.edit");
  const isCreateMode = !id;
  const isExistingManufacturer = Boolean(id && manufacturerData?.ID);

  const fetchManufacturer = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return null;
    }
    try {
      setLoading(true);
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(`getmanufacturerbyid?ID=${id}`);
      setManufacturerData(response?.data?.data || null);
      if (onManufacturerUpdated) {
        await onManufacturerUpdated();
      }
      return response?.data?.data || null;
    } finally {
      setLoading(false);
    }
  }, [id, onManufacturerUpdated]);

  useEffect(() => {
    if (isCreateMode) {
      setManufacturerData(
        manufacturerDetails || {
          ...emptyEditForm,
          IsActive: true,
          NeedsApproval: true,
          ASInvoice: false,
          CollectPayment: false,
        },
      );
      setLoading(false);
      setEditModalOpen(true);
      return;
    }

    if (manufacturerDetails) {
      setManufacturerData(manufacturerDetails);
      setLoading(false);
    } else {
      fetchManufacturer();
    }
  }, [fetchManufacturer, id, isCreateMode, manufacturerDetails]);

  const fullAddress = useMemo(() => {
    if (!manufacturerData) return [];
    return [
      manufacturerData.AddressLine1,
      manufacturerData.AddressLine2,
      [manufacturerData.City, manufacturerData.State].filter(Boolean).join(", "),
      manufacturerData.Zip,
    ].filter((item) => String(item || "").trim());
  }, [manufacturerData]);

  const handleDeactivate = async () => {
    const result = await Swal.fire({
      title: "Deactivate manufacturer?",
      text: "This will deactivate the manufacturer and delete its account links and account product rows for products owned by this manufacturer. Reactivation will not restore those relationships.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.failureColor,
      cancelButtonColor: colors.strokeDark,
      confirmButtonText: "Deactivate",
    });

    if (!result.isConfirmed) return;
    try {
      setStatusSubmitting(true);
      const apiServe = new APIService();
      await apiServe.updateDataToBackend(
        { ManufacturerID: manufacturerData.ID },
        "deactivatemanufacturer",
      );
      message.success("Manufacturer deactivated.");
      await fetchManufacturer();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to deactivate manufacturer.",
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setStatusSubmitting(true);
      const apiServe = new APIService();
      await apiServe.updateDataToBackend(
        { ManufacturerID: manufacturerData.ID },
        "reactivatemanufacturer",
      );
      message.success("Manufacturer reactivated.");
      await fetchManufacturer();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to reactivate manufacturer.",
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (!manufacturerData) {
    return (
      <div className="manufacturer-details-empty">
        <Text variant="smallText" color="lightText">
          Manufacturer details are unavailable.
        </Text>
      </div>
    );
  }

  const isActive = boolValue(manufacturerData.IsActive);

  return (
    <div className="manufacturer-details-page">
      <section className="manufacturer-summary-card">
        <div className="manufacturer-summary-grid">
          <div className="manufacturer-summary-column">
            <DetailItem label="Manufacturer Name">
              {displayValue(manufacturerData.ManufacturerName)}
            </DetailItem>
            <div className="manufacturer-two-detail-row">
              <DetailItem label="Created On">
                {formatDate(manufacturerData.createdAt)}
              </DetailItem>
              <DetailItem label="Deactivated On">
                {formatDate(manufacturerData.DeactivatedOn)}
              </DetailItem>
            </div>
          </div>

          <DetailItem label="Manufacturer Address">
            {fullAddress.length > 0 ? (
              <span className="manufacturer-address-lines">
                {fullAddress.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </span>
            ) : (
              "N/A"
            )}
          </DetailItem>

          <div className="manufacturer-summary-column compact">
            <DetailItem label="Phone">{displayValue(manufacturerData.Phone)}</DetailItem>
            <DetailItem label="Fax">{displayValue(manufacturerData.Fax)}</DetailItem>
          </div>

          <div className="manufacturer-summary-column emails">
            <DetailItem label="Direct Order Email">
              {displayValue(manufacturerData.ManufacturerEmailDO)}
            </DetailItem>
            <DetailItem label="Consignment Order Email">
              {displayValue(manufacturerData.ManufacturerEmailCO)}
            </DetailItem>
            <DetailItem label="Product Usage Email">
              {displayValue(manufacturerData.ManufacturerEmailPU)}
            </DetailItem>
          </div>

          <div className="manufacturer-summary-column compact">
            <DetailItem label="Needs Approval">
              <StatusPill active={boolValue(manufacturerData.NeedsApproval)} />
            </DetailItem>
            <DetailItem label="AS Invoice">
              <StatusPill active={boolValue(manufacturerData.ASInvoice)} />
            </DetailItem>
            <DetailItem label="Collect Payment">
              <StatusPill active={boolValue(manufacturerData.CollectPayment)} />
            </DetailItem>
          </div>
        </div>

        {isAdmin && isExistingManufacturer && (
          <div className="manufacturer-summary-actions">
            {canEditManufacturer && (
              <button
                className="manufacturer-action-button primary"
                onClick={() => setEditModalOpen(true)}
                type="button"
              >
                Edit
              </button>
            )}
            {hasPermission(userData, "manage_manufacturers.delete") && (
              isActive ? (
                <button
                  className="manufacturer-action-button danger"
                  disabled={statusSubmitting}
                  onClick={handleDeactivate}
                  type="button"
                >
                  Deactivate
                </button>
              ) : (
                <button
                  className="manufacturer-action-button success"
                  disabled={statusSubmitting}
                  onClick={handleReactivate}
                  type="button"
                >
                  Reactivate
                </button>
              )
            )}
          </div>
        )}
      </section>

      {isExistingManufacturer && (
        <ManufacturerFilesSection
          manufacturerID={manufacturerData.ID}
          isAdmin={isAdmin}
          canAddFile={canEditManufacturer}
        />
      )}

      {isExistingManufacturer && (
        <div className={`manufacturer-related-tables ${isAdmin ? "" : "single"}`}>
          <div className="manufacturer-related-table">
            <ManufacturerUsers
              manufacturerData={manufacturerData}
              canAddUser={canEditManufacturer}
            />
          </div>

          {isAdmin && (
            <div className="manufacturer-related-table">
              <ManufacturerAccounts
                manufacturerData={manufacturerData}
                canAddAccount={canEditManufacturer && isActive}
              />
            </div>
          )}
        </div>
      )}

      {isExistingManufacturer && (
        <ManufacturerProduct
          manufacturerData={manufacturerData}
          canEditManufacturer={canEditManufacturer}
        />
      )}

      <EditManufacturerModal
        manufacturer={manufacturerData}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSaved={fetchManufacturer}
        isCreateMode={isCreateMode}
      />
    </div>
  );
};

export default ManufacturerDetailsTab;
